import React from "react";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

/** Legacy backend unicode superscript that used to be baked into display_name. */
export const AUTO_TAG_UNICODE_MARK = "ᵀᴬᴳ";

/**
 * Strip legacy TAG marks from fields API display_name so the frontend can
 * render a consistent superscript TAG.
 */
export function stripAutoTagMarkFromDisplayName(label: string): string {
  return String(label ?? "")
    .replace(/\bTAG\b/gi, "")
    .replace(new RegExp(AUTO_TAG_UNICODE_MARK, "g"), "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Plain-string header for Data Grid manage-columns / a11y when HTML is unavailable.
 * Appends the legacy unicode mark after stripping any existing mark.
 */
export function plainAutoTagHeaderName(displayName: string): string {
  const base = stripAutoTagMarkFromDisplayName(displayName);
  if (!base) return AUTO_TAG_UNICODE_MARK;
  return `${base}${AUTO_TAG_UNICODE_MARK}`;
}

const autoTagSupSx: SxProps<Theme> = {
  ml: 0.35,
  fontSize: "0.65em",
  fontWeight: 600,
  letterSpacing: "0.04em",
  // Match legacy unicode ᵀᴬᴳ baked into display_name (same color as the label).
  color: "inherit",
  verticalAlign: "super",
};

type AutoTagDisplayLabelProps = Readonly<{
  /** Raw fields API display_name (may still include a legacy TAG mark). */
  label: string;
}>;

/** Renders `{displayName}` with a visual superscript TAG for dynamic auto-tag fields. */
export function AutoTagDisplayLabel({ label }: AutoTagDisplayLabelProps) {
  const base = stripAutoTagMarkFromDisplayName(label);
  return (
    <>
      {base}
      <Box component="sup" aria-hidden sx={autoTagSupSx}>
        TAG
      </Box>
    </>
  );
}
